(() => {
  if (window.eliteReviewsInitialized) return;
  window.eliteReviewsInitialized = true;

  const reviewSelector = '[data-review-id]';
  const byNewest = (a, b) => Number(b.dataset.date) - Number(a.dataset.date) || Number(b.dataset.reviewId) - Number(a.dataset.reviewId);

  function updateProductSchema(widget, cards, complete) {
    const productId = widget.dataset.productId;
    if (!/^[1-9]\d*$/.test(productId || '')) return;
    const node = Array.from(document.querySelectorAll('[data-elite-product-schema]'))
      .find(schema => schema.dataset.eliteReviewProductId === productId);
    if (!node) return;
    let product;
    try { product = JSON.parse(node.textContent); } catch { return; }
    if (product['@type'] !== 'Product') return;

    const scoped = Array.from(new Map(cards.filter(card => card.dataset.productId === productId)
      .map(card => [card.dataset.reviewId, card])).values());
    const rated = scoped.filter(card => Number.isInteger(Number(card.dataset.rating))
      && Number(card.dataset.rating) >= 1 && Number(card.dataset.rating) <= 5);
    const reviews = rated.slice().sort(byNewest).map(card => {
      const author = card.querySelector('.epc-review__author strong')?.textContent.trim();
      const body = card.querySelector('.epc-review__body')?.textContent.trim();
      if (!author || !body || !/^[1-9]\d*$/.test(card.dataset.reviewId || '')) return null;
      const review = {
        '@type': 'Review', '@id': `${location.origin}/pages/reviews#review-${card.dataset.reviewId}`,
        author: { '@type': 'Person', name: author }, reviewBody: body,
        reviewRating: { '@type': 'Rating', ratingValue: Number(card.dataset.rating), bestRating: 5, worstRating: 1 }
      };
      const title = card.querySelector('h3')?.textContent.trim();
      const date = card.querySelector('time')?.getAttribute('datetime');
      if (title) review.name = title;
      if (/^\d{4}-\d{2}-\d{2}$/.test(date || '')) review.datePublished = date;
      return review;
    });

    // A partial feed must not be presented as the complete product rating.
    if (complete && rated.length && reviews.every(Boolean)) {
      product.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: Number((rated.reduce((sum, card) => sum + Number(card.dataset.rating), 0) / rated.length).toFixed(1)),
        reviewCount: rated.length, bestRating: 5, worstRating: 1
      };
      // Keep the initial visible review sample small; the average uses every
      // rated review and the page's Show more control exposes the remainder.
      product.review = reviews.slice(0, 12);
    } else {
      delete product.aggregateRating;
      delete product.review;
    }
    const encoded = JSON.stringify(product).replace(/</g, '\\u003c');
    if (node.textContent === encoded) return;
    node.textContent = encoded;
    document.dispatchEvent(new CustomEvent('elite:product-reviews:updated', { detail: { productId } }));
  }

  function updateSummary(widget, cards, complete) {
    const rated = cards.filter(card => Number(card.dataset.rating) >= 1 && Number(card.dataset.rating) <= 5);
    const average = rated.length ? rated.reduce((sum, card) => sum + Number(card.dataset.rating), 0) / rated.length : 0;
    widget.dataset.ratingCounts = JSON.stringify([1, 2, 3, 4, 5].map(score => rated.filter(card => Number(card.dataset.rating) === score).length));
    widget.querySelector('[data-average]').textContent = rated.length ? average.toFixed(1) : '—';
    widget.querySelector('[data-summary-caption]').textContent = rated.length
      ? `Based on ${rated.length}${complete ? '' : ' recent'} rated ${rated.length === 1 ? 'review' : 'reviews'}`
      : complete ? 'No ratings yet' : 'No ratings in the loaded reviews';
    const published = widget.querySelector('[data-published-count]');
    if (published) {
      const count = widget.dataset.productId ? cards.length : Number(widget.dataset.total);
      published.textContent = `${count} ${widget.dataset.productId && !complete ? 'loaded' : 'published'} ${count === 1 ? 'review' : 'reviews'}`;
    }
    const stars = widget.querySelector('[data-average-stars]');
    stars.hidden = !rated.length;
    const visual = stars.querySelector('.epc-stars');
    visual.setAttribute('aria-label', `${average.toFixed(1)} out of 5 stars`);
    visual.querySelectorAll('span').forEach((star, index) => {
      // Fill fractional stars so a 4.3 average does not look like 4 or 5.
      const fill = Math.max(0, Math.min(1, average - index)) * 100;
      star.classList.remove('epc-stars__empty');
      star.style.background = `linear-gradient(90deg, #b96600 ${fill}%, #cbd2d8 ${fill}%)`;
      star.style.backgroundClip = 'text';
      star.style.color = 'transparent';
    });
  }

  async function initWidget(widget) {
    if (widget.dataset.ready) return;
    widget.dataset.ready = 'true';
    const home = widget.dataset.mode === 'home';
    const productId = widget.dataset.productId;
    const list = widget.querySelector('[data-review-list]');
    const source = widget.querySelector('[data-review-source]');
    let cards = Array.from((source ? source.content : list).querySelectorAll(reviewSelector));
    const total = Number(widget.dataset.total);
    const controls = widget.querySelector('[data-review-controls]');
    const filter = widget.querySelector('[data-filter]');
    const sort = widget.querySelector('[data-sort]');
    const more = widget.querySelector('[data-load-more]');
    const nativePagination = widget.querySelector('[data-native-pagination]');
    const notice = widget.querySelector('[data-load-notice]');
    let visibleCount = home ? 3 : 12;
    let complete = cards.length >= total;
    const productCards = () => cards.filter(card => !productId || card.dataset.productId === productId);

    function render() {
      const scopedCards = productCards();
      let selected = scopedCards.filter(card => !filter || filter.value === 'all' || card.dataset.rating === filter.value);
      const ordering = sort?.value || 'newest';
      selected.sort(ordering === 'highest'
        ? (a, b) => Number(b.dataset.rating) - Number(a.dataset.rating) || byNewest(a, b)
        : ordering === 'lowest'
          ? (a, b) => (Number(a.dataset.rating) || 6) - (Number(b.dataset.rating) || 6) || byNewest(a, b)
          : byNewest);
      list.replaceChildren(...selected.slice(0, visibleCount).map(card => card.cloneNode(true)));
      list.hidden = !selected.length;
      widget.querySelector('[data-review-empty]').hidden = scopedCards.length > 0 || !complete;
      if (controls) controls.hidden = !scopedCards.length;
      if (more) more.hidden = selected.length <= visibleCount;
      const noMatch = widget.querySelector('[data-no-match]');
      if (noMatch) noMatch.hidden = !scopedCards.length || selected.length > 0;
      const count = widget.querySelector('[data-results-count]');
      if (count) count.textContent = `${selected.length} ${selected.length === 1 ? 'review' : 'reviews'}${complete ? '' : ' loaded'}`;
      if (nativePagination) nativePagination.hidden = complete;
      updateSummary(widget, scopedCards, complete);
      updateProductSchema(widget, scopedCards, complete);
    }

    function revealLinkedReview() {
      if (home || !/^#review-\d+$/.test(window.location.hash)) return;
      const index = productCards().sort(byNewest).findIndex(card => `#${card.id}` === window.location.hash);
      if (index < 0) return;
      filter.value = 'all';
      sort.value = 'newest';
      visibleCount = Math.max(12, index + 1);
      render();
      widget.querySelector(window.location.hash)?.scrollIntoView({ block: 'center' });
    }

    function openProductReviewTab() {
      if (!productId) return;
      const url = new URL(window.location.href);
      if (!/^#(?:write-review|reviews|review-\d+)$/.test(url.hash) && !url.searchParams.has('page')) return;
      const panel = widget.closest('[data-product-tab-panel]');
      const section = panel?.closest('[data-product-tabs-section]');
      const tab = section?.querySelector('[data-s3-tab="reviews"]');
      if (tab?.getAttribute('aria-selected') !== 'true') tab?.click();
      if (url.hash === '#write-review') widget.querySelector('#write-review')?.scrollIntoView({ block: 'start' });
    }
    // The shared tab script may load after this widget. Use its readiness
    // event so direct review links also work with deferred script loading.
    const tabsSection = widget.closest('[data-product-tabs-section]');
    tabsSection?.addEventListener('elite:product-tabs-ready', openProductReviewTab);
    if (tabsSection?.dataset.productTabsReady === 'true') openProductReviewTab();
    window.addEventListener('hashchange', openProductReviewTab);

    [filter, sort].forEach(control => control?.addEventListener('change', () => { visibleCount = 12; render(); }));
    more?.addEventListener('click', () => { visibleCount += 12; render(); });
    widget.querySelector('[data-reset-filter]')?.addEventListener('click', () => { filter.value = 'all'; visibleCount = 12; render(); });
    render();
    revealLinkedReview();
    window.addEventListener('hashchange', revealLinkedReview);

    if (!complete) {
      if (notice) { notice.hidden = false; notice.textContent = 'Loading the rest of the reviews…'; }
      try {
        // Re-fetch from page one: a visitor may have arrived on any native
        // pagination page. Only public, escaped card HTML is requested.
        const collected = new Map();
        let page = 1;
        while (page) {
          const url = new URL(widget.dataset.feedUrl, window.location.origin);
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { signal: AbortSignal.timeout(15000) });
          if (!response.ok) throw new Error('Review feed unavailable');
          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const feed = documentFragment.querySelector('[data-review-feed]');
          if (!feed) throw new Error('Review feed missing');
          feed.querySelectorAll(reviewSelector).forEach(card => collected.set(card.dataset.reviewId, card));
          const nextPage = Number(feed.dataset.nextPage);
          if (nextPage && nextPage <= page) throw new Error('Invalid review pagination');
          page = nextPage;
        }
        if (collected.size < total) throw new Error('Incomplete review feed');
        cards = Array.from(collected.values());
        complete = true;
        if (notice) notice.hidden = true;
        render();
        revealLinkedReview();
      } catch {
        if (notice) notice.textContent = 'Some reviews could not be loaded. You can still use the page links below to read them.';
        // Keep the server-rendered subset, honest summary and native links.
      }
    }
  }

  function initForm(form) {
    if (form.dataset.ready) return;
    form.dataset.ready = 'true';
    const result = form.querySelector('[data-form-result]');
    if (result) result.focus();
    const body = form.querySelector('#ReviewBody');
    if (!body) return;
    const title = form.querySelector('#ReviewTitle');
    const encoded = form.querySelector('[data-encoded-body]');
    const ratings = Array.from(form.querySelectorAll('[name="review_rating"]'));
    const productFields = form.querySelector('[data-product-form-fields]');
    let productId = productFields?.dataset.productId || '';
    const previous = body.value.match(/^\[([1-5])\/5\]\s*([^\n]*)\n+([\s\S]*)$/);
    if (previous) {
      ratings.find(radio => radio.value === previous[1]).checked = true;
      title.value = previous[2];
      body.value = previous[3];
      const association = body.value.match(/^\[product:([1-9]\d*)\]\r?\n+([\s\S]*)$/);
      if (association) {
        productId = productId || association[1];
        body.value = association[2];
      }
    }
    if (productFields) { productFields.hidden = false; productFields.disabled = false; }
    form.querySelectorAll('[data-enhanced-fields]').forEach(fieldset => { fieldset.hidden = false; fieldset.disabled = false; });
    body.removeAttribute('name');
    encoded.disabled = false;
    const labels = ['Poor', 'Fair', 'Average', 'Good', 'Excellent'];

    function sync() {
      const rating = ratings.find(radio => radio.checked)?.value || '';
      ratings.forEach(radio => radio.closest('label').classList.toggle('is-filled', Number(radio.value) <= Number(rating)));
      form.querySelector('[data-rating-label]').textContent = rating ? `${rating} / 5 — ${labels[Number(rating) - 1]}` : 'Select a rating';
      const cleanTitle = title.value.trim().replace(/[\r\n]+/g, ' ');
      encoded.value = `[${rating}/5] ${cleanTitle}${productId ? `\n[product:${productId}]` : ''}\n\n${body.value.trim()}`;
      title.setCustomValidity(cleanTitle ? '' : 'Please add a review title.');
      body.setCustomValidity(body.value.trim().length >= 20 ? '' : 'Please write at least 20 characters about your experience.');
      const name = form.querySelector('#ReviewName');
      name.setCustomValidity(name.value.trim() ? '' : 'Please enter a display name.');
    }
    // Keep the native payload in sync before Shopify's CAPTCHA integration
    // submits the form, including flows that call form.submit() directly.
    form.addEventListener('input', sync);
    form.addEventListener('change', sync);
    form.addEventListener('submit', sync);
    sync();
  }

  function init(root = document) {
    root.querySelectorAll('[data-reviews-widget]').forEach(initWidget);
    root.querySelectorAll('.epc-review-form').forEach(initForm);
  }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
